import flagsmith from '@flagsmith/flagsmith';
import {ITraits} from '@flagsmith/flagsmith/types';
import {captureException} from '@sentry/browser';

import {useCallback, useEffect, useRef, useState} from 'react';

import {isNil} from 'ramda';

interface UseFlagSmithProps {
  environmentID: string;
  identity?: string;
  apiUrl: string;
  skip?: boolean;
  traits?: ITraits;
}

export function useFlagsmith({environmentID, identity, apiUrl, skip, traits}: UseFlagSmithProps) {
  const [isFlagSmithInitialized, setInitialized] = useState(false);

  // This is used to check if the feature flags are loaded from the API
  // and not from the local storage cache. Cached flags may not be up to date.
  const [areFeatureFlagsLoadedFromApi, setLoadedFromApi] = useState(false);

  // Set once the server fetch answers or `init` rejects, so the flags are as fresh as they will
  // get. It stays unset when `init` resolved from the cache and the later server fetch fails: that
  // failure reaches only an `onError` callback, and none is passed. A consumer that waits on it
  // needs its own timeout. Every init resets it; `areFeatureFlagsLoadedFromApi` is not reset.
  const [hasServerFetchSettled, setServerFetchSettled] = useState(false);

  const handleInitFlagsmith = useCallback(
    (userIdentity?: string, newTraits?: ITraits) => {
      setServerFetchSettled(false);
      flagsmith
        .init({
          environmentID,
          api: apiUrl,
          identity: userIdentity,
          cacheFlags: true,
          traits: newTraits,
          onChange: (_changes, params) => {
            if (params.isFromServer) {
              setLoadedFromApi(true);
              setServerFetchSettled(true);
            }
          },
        })
        .then(() => setInitialized(true))
        .catch((error: Error) => {
          // Flagsmith sometimes throws an error with message 'No error message' without any other information
          if (error?.message !== 'No error message' || isNil(error?.message)) {
            console.error('Error when initializing Flagsmith:', error);
            captureException(error);
          }
          // v10+ rejects here when cacheFlags is enabled and the API is unreachable without
          // cache. Keep the old startup behavior: flags read as false, but the app can render.
          setServerFetchSettled(true);
          setInitialized(true);
        });
    },
    [environmentID, apiUrl]
  );

  useOnMount(() => {
    if (skip) {
      return;
    }
    handleInitFlagsmith(identity, traits);
  });

  return {
    isFlagSmithInitialized,
    handleInitFlagsmith,
    areFeatureFlagsLoadedFromApi,
    hasServerFetchSettled,
  };
}

const useOnMount = (func?: () => void) => {
  const didMountRef = useRef(false);

  useEffect(function runFuncOnFirstMount() {
    if (didMountRef.current === false) {
      func?.();
      didMountRef.current = true;
    }

    return () => {
      didMountRef.current = false;
    };
  }, []); // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
};
